import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  Alert,
  Box,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
} from '@mui/material';
import { TokenAwareTextField as TextField } from './TokenAwareTextField';
import { TokenFieldProvider } from './TokenFieldContext';
import { useCampaignMutations } from '../../../../hooks/campaign-hooks';
import {
  COMPLETION_MODES,
  Campaign,
  CampaignStep,
  CompletionMode,
  FileTypeCategory,
  HelpLink,
  SELF_ATTEST_KINDS,
  STEP_KINDS,
  STEP_TRACKS,
  StepChecklist,
  StepFileRef,
  StepKind,
  StepQuestion,
  StepQuestionRef,
  StepTrack,
  helpLinksOf,
  nextOrder,
  stepFileUploadConfig,
  stepSalesforceDriven,
  stepTrail,
} from '../../../../support/campaigns';
import {
  GhostButton,
  PrimaryButton,
  SectionHeader,
  builderStyles,
} from './primitives';
import { CollapsibleSection, FullScreenOverlay, SummaryRow } from './Disclosure';
import StepKindFields from './StepKindFields';
import FileUploadFields from './FileUploadFields';
import { LinkListEditor } from './LinkListEditor';
import {
  RunnerContentFields,
  RunnerContentValues,
  materializeRunnerDefaults,
  readRunnerContent,
} from './RunnerContentFields';
import {
  StepFilesFields,
  cleanStepFiles,
  readStepFiles,
} from './StepFilesFields';
import {
  StepQuestionsFields,
  cleanStepQuestionRefs,
  readStepQuestionRefs,
} from './StepQuestionsFields';
import { ContentBlocksEditor } from './ContentBlocksEditor';
import { StepLayoutComposer } from './StepLayoutComposer';
import { WorkshopPrepConfigFields } from './WorkshopPrepConfigFields';
import {
  WorkshopPrepConfig,
  resolveWorkshopPrepConfig,
} from '../../../Campaigns/workshopPrepConfig';
import { ReportTemplateWorkspace } from '../ReportTemplateBuilder';
import { NestedContentEditor } from './NestedContentEditor';
import { STEP_CONTENT_CATEGORIES, OPTIONAL_CONTENT_SECTIONS, CONTENT_VARIANTS } from './stepContentCategories';
import { NESTED_CONTENT_TREES } from './nestedContentTrees';
import StepPreview from './StepPreview';
import DraftBanner from './ai/DraftBanner';
import AiFieldNote from './ai/AiFieldNote';
import { useStepDescriptionDraft } from '../ai/useCampaignAi';

/**
 * Step create/edit/delete form for the Campaign Builder admin. Adapted from the user-space StepForm:
 * same field set and full-record write logic (create posts the whole record; edit spreads the fetched
 * step and overrides the edited fields — a delta PUT 400s on the Go runtime). The raw numeric-id
 * journey_map/assessment TextFields are replaced with the real pickers via StepKindFields, and AI
 * assist surfaces (a draftable description + a name field-note) are layered on.
 */

interface StepFormValues {
  name: string;
  description: string;
  track: StepTrack;
  step_kind: StepKind;
  completion_mode: CompletionMode;
  is_required: boolean;
  blocks_progression: boolean;
  /** Hides the step (and, for a group, its subtree) from the runner without deleting it. */
  hidden: boolean;
  estimated_duration_minutes: string;
  journey_map: number | null;
  assessment: number | null;
  launch_url: string;
  provider: string;
  /** Authored question set for a `question` step; persisted to `metadata.questions`. */
  questions: StepQuestion[];
  /** The content template this content step adopts (selects its layout + content manifest); persisted to `metadata.runnerKind`. */
  runner_kind: string;
  /**
   * Copy + Webex defaults for the workshop-prep ("Schedule") step; persisted to
   * `metadata.workshopPrepConfig`. Always the fully-resolved config (defaults merged in), so the
   * three prep sections show effective values and a save writes the whole authored object.
   */
  workshop_prep: WorkshopPrepConfig;
  /** DB-authored content for a templated content step; persisted to `metadata.runner`. */
  runner: RunnerContentValues;
  /** Generic step → campaign-asset file references; persisted to `metadata.files`. */
  files: StepFileRef[];
  /** Generic step → question content-block references; persisted to `metadata.questionRefs`. */
  questionRefs: StepQuestionRef[];
  /** Self-attest checklist for content/action_item/followup; persisted to `metadata.checklist`. */
  checklist: StepChecklist;
  /** Flags this checklist step as the campaign's first-step gate; persisted to `metadata.gatesCampaign`. */
  gates_campaign: boolean;
  /** Sub-step / checklist-item layout for group + self-attest steps; persisted to `metadata.layout`. */
  layout: string;
  /** Optional per-step help/resource links; persisted to `metadata.helpLinks`. */
  help_links: HelpLink[];
  /** Opt-in optional content sections (e.g. FAQ) enabled on this step; persisted to `metadata.contentSections`. */
  content_sections: string[];
  /**
   * For a per-delivery top-level group: whether each delivery is Salesforce Account/Opportunity–driven
   * (SF picker) vs. free-titled (one-to-many event); persisted to `metadata.salesforceDriven`.
   */
  salesforce_driven: boolean;
  /** Whether this content-bearing step collects file uploads; the presence of `metadata.fileUpload`. */
  file_upload_enabled: boolean;
  /** File-upload section heading; persisted to `metadata.fileUpload.title`. */
  file_upload_title: string;
  /** File-upload section prose; persisted to `metadata.fileUpload.description`. */
  file_upload_description: string;
  /** Allowed file-type categories for the upload section; persisted to `metadata.fileUpload.allowedTypes`. */
  file_upload_types: FileTypeCategory[];
  /** Whether ≥1 upload is required to complete the step; persisted to `metadata.fileUpload.required`. */
  file_upload_required: boolean;
  /** The step's parent group id (edit-mode re-parenting); null = top level. Ignored on create. */
  parent: number | null;
}

/**
 * The content "template" a `content` step can adopt (persisted as `metadata.runnerKind`); '' = a plain
 * content step. A template is a data discriminator only — it selects which composition layout + content
 * manifest the step authors, NOT a separate render path or a specific campaign. Its options and labels
 * are DB-shaped data in `CONTENT_VARIANTS` (derived from `STEP_CONTENT_CATEGORIES`), so a new template is
 * a one-file data edit. Only `content` steps expose it; every other kind renders purely from its
 * `step_kind`.
 */
const RUNNER_VARIANT_KINDS = new Set<StepKind>(['content']);

/**
 * Which config sections are relevant to each step kind — the WS2 "type-relevant sections only" gate.
 * Essentials (name/description/track/kind/parent), Behavior & completion, Activity settings, Help &
 * resource links, and Preview are shown for every kind; the sections below are shown only where they
 * carry meaning, so e.g. a journey_map step shows just its journey-map reference (Activity settings),
 * not "Files & questions."
 */
const SECTION_RELEVANCE: Record<string, Set<StepKind>> = {
  // Generic campaign-asset files + question-block references: authored content-bearing kinds only.
  filesAndQuestions: new Set<StepKind>(['content', 'action_item', 'followup']),
  // The optional per-delivery file-upload section: the same content-bearing kinds.
  fileUploads: new Set<StepKind>(['content', 'action_item', 'followup']),
};

const sectionRelevant = (section: keyof typeof SECTION_RELEVANCE, kind: StepKind): boolean =>
  SECTION_RELEVANCE[section]?.has(kind) ?? false;

/** Read the authored question array off a step's metadata (bare array or `{items}`), for the form. */
const readStepQuestions = (step?: CampaignStep): StepQuestion[] => {
  const raw = (step?.metadata as any)?.questions;
  if (Array.isArray(raw)) return raw as StepQuestion[];
  if (raw && Array.isArray(raw.items)) return raw.items as StepQuestion[];
  return [];
};

/** Read the authored checklist off a step's metadata (bare array or `{lead, items}`), for the form. */
const readStepChecklist = (step?: CampaignStep): StepChecklist => {
  const raw = (step?.metadata as any)?.checklist;
  if (Array.isArray(raw)) return { items: raw };
  if (raw && Array.isArray(raw.items)) return { lead: raw.lead, items: raw.items };
  return { items: [] };
};

const toValues = (step?: CampaignStep, fallbackTrack: StepTrack = 'enablement'): StepFormValues => {
  const fileUpload = stepFileUploadConfig(step);
  return {
  name: step?.name ?? '',
  description: step?.description ?? '',
  track: step?.track ?? fallbackTrack,
  step_kind: step?.step_kind ?? 'content',
  completion_mode: step?.completion_mode ?? 'once',
  is_required: step?.is_required ?? true,
  blocks_progression: step?.blocks_progression ?? false,
  hidden: step?.hidden ?? false,
  estimated_duration_minutes:
    step?.estimated_duration_minutes != null ? String(step.estimated_duration_minutes) : '',
  journey_map: step?.journey_map ?? null,
  assessment: step?.assessment ?? null,
  launch_url: step?.launch_url ?? '',
  provider: step?.provider ?? '',
  questions: readStepQuestions(step),
  runner_kind: (step?.metadata as any)?.runnerKind ?? '',
  workshop_prep: resolveWorkshopPrepConfig((step?.metadata as any)?.workshopPrepConfig),
  runner: materializeRunnerDefaults(
    readRunnerContent((step?.metadata as any)?.runner),
    (step?.metadata as any)?.runnerKind ?? '',
  ),
  files: readStepFiles((step?.metadata as any)?.files),
  questionRefs: readStepQuestionRefs((step?.metadata as any)?.questionRefs),
  checklist: readStepChecklist(step),
  gates_campaign: !!(step?.metadata as any)?.gatesCampaign,
  layout:
    (step?.metadata as any)?.layout === 'grid-2'
      ? 'grid-2'
      : (step?.metadata as any)?.layout === 'grid-3'
      ? 'grid-3'
      : 'list',
  help_links: helpLinksOf(step),
  content_sections: Array.isArray((step?.metadata as any)?.contentSections)
    ? ((step?.metadata as any).contentSections as unknown[]).filter(
        (c): c is string => typeof c === 'string',
      )
    : [],
  salesforce_driven: stepSalesforceDriven(step),
  file_upload_enabled: fileUpload != null,
  file_upload_title: fileUpload?.title ?? '',
  file_upload_description: fileUpload?.description ?? '',
  file_upload_types: fileUpload?.allowedTypes ?? ['any'],
  file_upload_required: fileUpload?.required ?? false,
  parent: step?.parent ?? null,
  };
};

/** Checklist items with both a key and a label — the ones worth persisting / gating on. */
const validChecklistItems = (checklist: StepChecklist | undefined) =>
  (checklist?.items ?? []).filter((it) => it && it.key && it.label);

// Kinds that carry a completion signal the runner can gate on. The leaf kinds mirror
// useCampaignStepStatus.COMPLETION_TRACKED; `group` gates on its aggregated child completion
// (all children done — useCampaignStepStatus rollup), so a group of journey-map courses can act as a
// soft training gate. Only these expose "Block progression until complete" (Next-only; never blocks
// opening — that is the separate hard `gatesCampaign` redirect).
const GATEABLE_KINDS = new Set<StepKind>(['assessment', 'journey_map', 'moodle_workshop', 'group']);

const numOrNull = (v: string): number | null => {
  const t = (v || '').trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

export function StepDetailForm(props: {
  campaign: Campaign;
  /** The step being edited; omit for create mode. */
  step?: CampaignStep;
  /** Parent step id for a new (sub-)step; null creates a root step. Ignored when editing. */
  parentId?: number | null;
  /** The full flat step set, for `order` computation and child detection. */
  allSteps: CampaignStep[];
  /** When false, every field is disabled and save/delete are hidden. */
  canWrite: boolean;
  onSaved: (step: CampaignStep) => void;
  /** Called after a successful delete (edit mode only). */
  onDeleted?: () => void;
  onCancel?: () => void;
}): JSX.Element {
  const { campaign, step, parentId = null, allSteps, canWrite, onSaved, onDeleted, onCancel } = props;
  const isEdit = !!step;
  const { create, update, remove, isSaving, error } = useCampaignMutations('step');

  // A new sub-step inherits the context it was created from. Placement can be nested arbitrarily
  // deep, so the track default comes from the branch's TOP-LEVEL group (the track owner) rather than
  // just the immediate parent — otherwise a sub-step three levels down could pick up an intermediate
  // group's stray track and drift off-branch. The immediate parent is still used for the modal title.
  // A root "Add step" has no parent, so it keeps the plain default.
  const ancestry = parentId != null ? stepTrail(allSteps, parentId) : [];
  const parentStep = ancestry.length ? ancestry[ancestry.length - 1] : undefined;
  const contextTrack = ancestry.length ? ancestry[0].track : 'enablement';

  // `completion_mode` (one-time vs per-delivery) is a property of a TOP-LEVEL step only: a top-level
  // step is either completed once by the SE or fans out into many deliveries, and its sub-steps ride
  // whichever mode the parent chose. So the control is shown only for root steps; sub-steps stay
  // 'once' and never expose the choice.
  const isTopLevel = isEdit ? step?.parent == null : parentId == null;

  // Re-parenting (edit mode): the valid new parents are the campaign's *group* steps, minus this step
  // itself and its own descendants (which would form a cycle). The runner nests only under groups, so
  // the picker mirrors that convention. This is the canvas's cross-level move — the visual board's
  // per-lane drag can reorder and the nested lists can re-parent between groups, but promoting a
  // sub-step to the top level (or nesting a top-level step) is done here, robustly, by field.
  const descendantIds = React.useMemo(() => {
    const out = new Set<number>();
    if (!step) return out;
    const walk = (pid: number) => {
      allSteps.forEach((s) => {
        if ((s.parent ?? null) === pid && !out.has(s.id)) {
          out.add(s.id);
          walk(s.id);
        }
      });
    };
    walk(step.id);
    return out;
  }, [allSteps, step]);

  const parentOptions = React.useMemo(
    () =>
      allSteps.filter(
        (s) => s.step_kind === 'group' && s.id !== step?.id && !descendantIds.has(s.id)
      ),
    [allSteps, step, descendantIds]
  );

  const { control, handleSubmit, setValue, watch } = useForm<StepFormValues>({
    defaultValues: toValues(step, contextTrack),
  });

  const stepKind = watch('step_kind');
  const completionMode = watch('completion_mode');
  const runnerKind = watch('runner_kind');
  // The workshop-prep ("Schedule") template is authored through three dedicated sections (internal
  // team / customer personas / Webex scheduling) that mirror the runtime `WorkshopPrepPanel`, NOT
  // the generic content-template stack (body copy / layout primitives / content blocks). The
  // report-template + optional-content-section rows are likewise irrelevant here.
  const isPrepStep = RUNNER_VARIANT_KINDS.has(stepKind) && runnerKind === 'prepare';

  // The per-delivery SF-driven toggle is only meaningful on a per-delivery top-level group.
  const showSalesforceDriven =
    isTopLevel && stepKind === 'group' && completionMode === 'per_delivery';

  // Whether this step lives in the per-delivery flow — a top-level step's own completion mode, or (for a
  // sub-step) the completion mode of its top-level ancestor. Delivery-scoped tokens (customer /
  // Salesforce / captured answers / gaps / deep-dive) can only resolve here, so the token picker offers
  // them only when the step is per-delivery; an enablement step is limited to campaign-scoped tokens.
  const topLevelAncestor = React.useMemo(() => {
    const startId = isEdit ? step?.parent ?? null : parentId;
    if (startId == null) return undefined; // this step is itself top-level
    const trail = stepTrail(allSteps, startId);
    return trail.length ? trail[0] : undefined;
  }, [isEdit, step, parentId, allSteps]);
  const topLevelCompletionMode: CompletionMode = isTopLevel
    ? completionMode
    : topLevelAncestor?.completion_mode ?? 'once';
  const fieldScope = topLevelCompletionMode === 'per_delivery' ? 'delivery' : 'campaign';

  // A self-attest step with checklist items can gate too (a per-step or first-step gate), so the
  // "Block progression" toggle and the gate flag extend beyond the completion-tracked kinds.
  const watchedChecklist = watch('checklist');
  const hasChecklistItems = validChecklistItems(watchedChecklist).length > 0;
  const canGate =
    GATEABLE_KINDS.has(stepKind) || (SELF_ATTEST_KINDS.has(stepKind) && hasChecklistItems);

  // AI-drafted description is armed only when the admin clicks "Draft description with AI".
  const [draftEnabled, setDraftEnabled] = React.useState(false);
  // Live read-only preview of what the step renders in the runner (off by default; the modal is tight).
  const [showPreview, setShowPreview] = React.useState(false);
  // The report builder is a page-scale tool; it opens in a full-screen overlay, not inline in the dock.
  const [reportOpen, setReportOpen] = React.useState(false);
  const watched = watch();
  const draftStep: CampaignStep =
    step ??
    ({
      id: -1,
      campaign: campaign.id,
      parent: parentId,
      order: 0,
      name: watched.name,
      description: watched.description || null,
      is_required: watched.is_required,
      blocks_progression: watched.blocks_progression,
      estimated_duration_minutes: numOrNull(watched.estimated_duration_minutes),
      track: watched.track,
      step_kind: watched.step_kind,
      completion_mode: watched.completion_mode,
      journey_map: watched.journey_map,
      assessment: watched.assessment,
      child_type: null,
      child_id: null,
      launch_url: watched.launch_url || null,
      provider: watched.provider || null,
      provider_config: {},
      metadata: {},
    } as CampaignStep);

  const descDraft = useStepDescriptionDraft({
    campaign,
    step: draftStep,
    enabled: draftEnabled,
  });

  const onSubmit = (values: StepFormValues) => {
    // Preserve any metadata the step already carries and fold in the fields the builder authors:
    // the content template, the question set, the self-attest checklist + first-step gate flag,
    // and the two-up layout. On a full-record PUT this metadata rides in `common`, overriding `...step`.
    const existingMeta =
      step?.metadata && typeof step.metadata === 'object'
        ? { ...(step.metadata as Record<string, any>) }
        : {};
    const metadata: Record<string, any> = { ...existingMeta };

    // Content template key (or clear it when set back to "None").
    if (values.runner_kind) metadata.runnerKind = values.runner_kind;
    else delete metadata.runnerKind;

    // Workshop-prep ("Schedule") step: the step-scoped copy + Webex defaults the runtime
    // `WorkshopPrepPanel` reads. Write the full authored config (all fields, defaults included) so
    // the surface is DB-driven; clear it entirely when the step no longer adopts the prepare
    // template. The team roles + personas the sections reference are campaign-level reference
    // objects persisted independently by their embedded editors.
    if (values.runner_kind === 'prepare') metadata.workshopPrepConfig = values.workshop_prep;
    else delete metadata.workshopPrepConfig;

    // DB-authored templated step content (metadata.runner). Only meaningful for a templated content
    // step: fold in the non-empty authored fields. Cleared entirely when the step no longer adopts a
    // template.
    if (values.runner_kind) {
      const existingRunner =
        existingMeta.runner && typeof existingMeta.runner === 'object'
          ? { ...(existingMeta.runner as Record<string, any>) }
          : {};
      const f = values.runner;
      const prompts = (f.quickPrompts ?? []).map((p) => p.trim()).filter(Boolean);
      const authored: Record<string, any> = { ...existingRunner };
      const setOrDrop = (key: string, val: string) => {
        if (val.trim()) authored[key] = val.trim();
        else delete authored[key];
      };
      setOrDrop('tag', f.tag);
      setOrDrop('title', f.title);
      setOrDrop('subtitle', f.subtitle);
      setOrDrop('cta', f.cta);
      setOrDrop('guideTitle', f.guideTitle);
      setOrDrop('guideBody', f.guideBody);
      // In-step section headers (metadata.runner.sectionHeadings), keyed by section id. Fold in the
      // non-empty title/lead per section; drop empty entries and clear the map entirely when none.
      const sh: Record<string, { title?: string; lead?: string }> = {};
      for (const [id, v] of Object.entries(f.sectionHeadings ?? {})) {
        const title = (v?.title ?? '').trim();
        const lead = (v?.lead ?? '').trim();
        const entry: { title?: string; lead?: string } = {};
        if (title) entry.title = title;
        if (lead) entry.lead = lead;
        if (Object.keys(entry).length) sh[id] = entry;
      }
      if (Object.keys(sh).length) authored.sectionHeadings = sh;
      else delete authored.sectionHeadings;
      // liveEligible is a real boolean choice, so it is always written (not fallback-on-blank).
      authored.liveEligible = !!f.liveEligible;
      if (prompts.length) authored.quickPrompts = prompts;
      else delete authored.quickPrompts;
      if (Object.keys(authored).length) metadata.runner = authored;
      else delete metadata.runner;
    } else {
      delete metadata.runner;
    }

    // Generic step → campaign-asset file references (metadata.files). Write the slot/asset rows that
    // are fully specified; clear the key entirely when none are. Applies to every step kind.
    const files = cleanStepFiles(values.files);
    if (files.length) metadata.files = files;
    else delete metadata.files;

    // Generic step → question content-block references (metadata.questionRefs). Write the fully
    // specified (category + key) rows; clear entirely when none. Applies to every step kind.
    const questionRefs = cleanStepQuestionRefs(values.questionRefs);
    if (questionRefs.length) metadata.questionRefs = questionRefs;
    else delete metadata.questionRefs;

    // Lightweight question set (question kind only).
    if (values.step_kind === 'question') metadata.questions = values.questions;

    // Self-attest checklist + first-step gate flag (checklist-bearing kinds only).
    const selfAttest = SELF_ATTEST_KINDS.has(values.step_kind);
    const items = selfAttest ? validChecklistItems(values.checklist) : [];
    if (items.length) {
      metadata.checklist = values.checklist.lead ? { lead: values.checklist.lead, items } : { items };
    } else {
      delete metadata.checklist;
    }
    if (selfAttest && values.gates_campaign && items.length) metadata.gatesCampaign = true;
    else delete metadata.gatesCampaign;

    // Side-by-side layout for group + self-attest steps (two- or three-up); drives the runner's
    // child grid (e.g. journey-map courses rendered side by side under a group).
    if (
      (values.step_kind === 'group' || selfAttest) &&
      (values.layout === 'grid-2' || values.layout === 'grid-3')
    ) {
      metadata.layout = values.layout;
    } else {
      delete metadata.layout;
    }

    // Optional per-step help/resource links (replaces the runner's hard-coded training video link).
    const stepLinks = (values.help_links ?? []).filter((l) => l && l.url.trim());
    if (stepLinks.length) {
      metadata.helpLinks = stepLinks.map((l) => ({ label: l.label.trim() || l.url.trim(), url: l.url.trim() }));
    } else {
      delete metadata.helpLinks;
    }

    // Opt-in optional content sections (e.g. FAQ). Only meaningful on a content step; write the set of
    // enabled section categories that are actually offered, else clear the key.
    const enabledSections =
      values.step_kind === 'content'
        ? (values.content_sections ?? []).filter((c) =>
            OPTIONAL_CONTENT_SECTIONS.some((s) => s.category === c),
          )
        : [];
    if (enabledSections.length) metadata.contentSections = enabledSections;
    else delete metadata.contentSections;

    // Per-delivery Salesforce-driven flag: only meaningful on a per-delivery top-level group. ON →
    // each delivery picks a real SF account/opportunity; OFF → the SE titles a one-to-many event.
    if (isTopLevel && values.step_kind === 'group' && values.completion_mode === 'per_delivery') {
      metadata.salesforceDriven = values.salesforce_driven;
    } else {
      delete metadata.salesforceDriven;
    }

    // Optional file-upload section: the runner adds an uploader to this step's body when
    // `metadata.fileUpload` is present. Written only when the author enabled it on a content-bearing
    // kind; cleared otherwise so a stale config left after toggling off or switching kind never leaks
    // through. Empty/absent types collapse to ['any'] (no restriction) to match stepFileUploadConfig.
    const fileUploadRelevant = sectionRelevant('fileUploads', values.step_kind);
    if (fileUploadRelevant && values.file_upload_enabled) {
      const allowedTypes = Array.isArray(values.file_upload_types) && values.file_upload_types.length
        ? Array.from(new Set(values.file_upload_types))
        : (['any'] as FileTypeCategory[]);
      metadata.fileUpload = {
        title: values.file_upload_title.trim(),
        description: values.file_upload_description.trim(),
        allowedTypes,
        required: values.file_upload_required === true,
      };
    } else {
      delete metadata.fileUpload;
    }

    // Gating: completion-tracked kinds, or a checklist-bearing self-attest step, may block Next until
    // done; a first-step gate always blocks its own Next too. Force off otherwise so a stale toggle
    // (e.g. after switching kind) never blocks a step with no completion signal.
    const gateable = GATEABLE_KINDS.has(values.step_kind) || (selfAttest && items.length > 0);
    const blocksProgression =
      selfAttest && values.gates_campaign && items.length > 0
        ? true
        : gateable
        ? values.blocks_progression
        : false;

    const common = {
      name: values.name.trim(),
      description: values.description.trim() || null,
      track: values.track,
      step_kind: values.step_kind,
      // Only a top-level step carries a completion mode; sub-steps always ride the parent's, so
      // they are stored 'once' and never surface the choice.
      completion_mode: isTopLevel ? values.completion_mode : 'once',
      is_required: values.is_required,
      blocks_progression: blocksProgression,
      hidden: values.hidden,
      estimated_duration_minutes: numOrNull(values.estimated_duration_minutes),
      journey_map: values.journey_map ?? null,
      assessment: values.assessment ?? null,
      launch_url: values.launch_url.trim() || null,
      provider: values.provider.trim() || null,
      metadata,
    };

    if (isEdit && step) {
      // Full-record PUT: spread the fetched row, override the edited fields. Re-parenting (the Parent
      // picker) rides here too; when the parent actually changes, drop the step at the end of the new
      // parent's list so it never collides with an existing sibling's order.
      const nextParent = values.parent ?? null;
      const reParented = nextParent !== (step.parent ?? null);
      const payload = {
        ...step,
        ...common,
        parent: nextParent,
        ...(reParented ? { order: nextOrder(allSteps, nextParent) } : {}),
        id: step.id,
      };
      update.mutate(payload, {
        onSuccess: (row: CampaignStep) => onSaved(row ?? (payload as CampaignStep)),
      });
    } else {
      const payload = {
        campaign: campaign.id,
        parent: parentId ?? null,
        order: nextOrder(allSteps, parentId ?? null),
        child_type: null,
        child_id: null,
        provider_config: {},
        metadata: {},
        ...common,
      };
      create.mutate(payload, {
        onSuccess: (row: CampaignStep) => onSaved(row),
      });
    }
  };

  const onDelete = () => {
    if (!step) return;
    const childCount = allSteps.filter((s) => s.parent === step.id).length;
    const message =
      childCount > 0
        ? `Delete this step? Its ${childCount} sub-step${
            childCount === 1 ? '' : 's'
          } will move to the top level (they are not deleted).`
        : 'Delete this step?';
    // eslint-disable-next-line no-alert
    if (window.confirm(message)) {
      remove.mutate(step.id, { onSuccess: onDeleted ?? undefined });
    }
  };

  return (
    <TokenFieldProvider scope={fieldScope}>
    <div className={builderStyles.contentCard}>
      <SectionHeader
        compact
        title={
          step?.name ||
          (parentId != null
            ? parentStep
              ? `New sub-step in “${parentStep.name}”`
              : 'New sub-step'
            : 'New step')
        }
      />

        <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
          <Stack spacing={2}>
            {!!error && <Alert severity="error">Could not save the step. Please try again.</Alert>}

            <Controller
              name="name"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Step name"
                  size="small"
                  required
                  disabled={!canWrite}
                  error={!!fieldState.error}
                  fullWidth
                />
              )}
            />

            {canWrite && (
              <AiFieldNote
                entity="step"
                field="name"
                context={{
                  campaign: campaign.name,
                  name: watched.name,
                  track: watched.track,
                  step_kind: watched.step_kind,
                }}
                onUse={(v) => setValue('name', v)}
              />
            )}

            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Description"
                  size="small"
                  multiline
                  minRows={2}
                  disabled={!canWrite}
                  fullWidth
                />
              )}
            />

            {canWrite && (
              <Stack spacing={1}>
                <Box>
                  <GhostButton
                    onClick={() => setDraftEnabled(true)}
                    disabled={descDraft.isLoading}
                  >
                    ✨ Draft description with AI
                  </GhostButton>
                </Box>
                <DraftBanner
                  label="AI-drafted description"
                  draft={descDraft.data}
                  isLoading={descDraft.isLoading}
                  onApply={(v) => setValue('description', v)}
                  onRefresh={descDraft.refresh}
                />
              </Stack>
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Controller
                name="track"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    label="Track"
                    size="small"
                    disabled={!canWrite}
                    fullWidth
                  >
                    {STEP_TRACKS.map((t) => (
                      <MenuItem key={t.value} value={t.value}>
                        {t.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
              <Controller
                name="step_kind"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    label="Kind"
                    size="small"
                    disabled={!canWrite}
                    fullWidth
                    helperText={
                      field.value === 'group'
                        ? 'A Group holds sub-steps — add them from the tree after saving.'
                        : 'Choose Group to nest sub-steps under this step.'
                    }
                  >
                    {STEP_KINDS.map((k) => (
                      <MenuItem key={k.value} value={k.value}>
                        {k.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Stack>

            {isEdit && (
              <Controller
                name="parent"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    label="Parent"
                    size="small"
                    disabled={!canWrite}
                    fullWidth
                    value={field.value == null ? '' : field.value}
                    onChange={(e) =>
                      field.onChange(e.target.value === '' ? null : Number(e.target.value))
                    }
                    helperText="Move this step under a group, or to the top level. Re-parenting places it last among the new parent's steps."
                  >
                    <MenuItem value="">None (top level)</MenuItem>
                    {parentOptions.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.name || `Step ${p.id}`}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            )}

            {RUNNER_VARIANT_KINDS.has(stepKind) && (
              <Controller
                name="runner_kind"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    label="Content template"
                    size="small"
                    disabled={!canWrite}
                    fullWidth
                    helperText="Optional template for this content step, or leave as a standard content step. A template just selects the step's layout primitives + content sections — it is data-driven, not a separate code path."
                  >
                    {CONTENT_VARIANTS.map((k) => (
                      <MenuItem key={k.value || 'none'} value={k.value}>
                        {k.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            )}

            {isPrepStep && (
              <Controller
                name="workshop_prep"
                control={control}
                render={({ field }) => {
                  const cfg = field.value as WorkshopPrepConfig;
                  return (
                    <Stack spacing={2}>
                      <CollapsibleSection
                        title="Internal team"
                        subtitle="The copy over the account-team block, and which of the campaign's team roles this workshop offers. Manage the role catalogue in the References tab."
                        accent
                        defaultOpen
                      >
                        <WorkshopPrepConfigFields
                          section="team"
                          value={cfg}
                          onChange={field.onChange}
                          disabled={!canWrite}
                          campaignId={campaign.id}
                        />
                      </CollapsibleSection>

                      <CollapsibleSection
                        title="Customer personas"
                        subtitle="The copy over the persona block, the priority wording, and which of the campaign's personas this workshop wants (each with a priority). Manage the persona catalogue in the References tab."
                        accent
                      >
                        <WorkshopPrepConfigFields
                          section="personas"
                          value={cfg}
                          onChange={field.onChange}
                          disabled={!canWrite}
                          campaignId={campaign.id}
                        />
                      </CollapsibleSection>

                      <CollapsibleSection
                        title="Webex scheduling"
                        subtitle="Copy, recording notice, meeting lengths, contact-title choices, and the meeting-title template for the Webex booking surface."
                        accent
                      >
                        <WorkshopPrepConfigFields
                          section="schedule"
                          value={cfg}
                          onChange={field.onChange}
                          disabled={!canWrite}
                          campaignId={campaign.id}
                        />
                      </CollapsibleSection>

                      {STEP_CONTENT_CATEGORIES[runnerKind]?.categories.length > 0 && (
                        <CollapsibleSection
                          title="Guide grounding &amp; roster"
                          subtitle="Prerequisite questions the Workshop Guide/AI grounds on, plus the stakeholder-priority roster. Not shown on the Schedule surface itself."
                        >
                          <ContentBlocksEditor
                            campaign={campaign}
                            canWrite={canWrite}
                            only={STEP_CONTENT_CATEGORIES[runnerKind].categories}
                            categoryLabels={STEP_CONTENT_CATEGORIES[runnerKind].labels}
                            title={STEP_CONTENT_CATEGORIES[runnerKind].title}
                            subtitle={STEP_CONTENT_CATEGORIES[runnerKind].subtitle}
                          />
                        </CollapsibleSection>
                      )}
                    </Stack>
                  );
                }}
              />
            )}

            {!!runnerKind && RUNNER_VARIANT_KINDS.has(stepKind) && !isPrepStep && (
              <CollapsibleSection
                title="Content template details"
                subtitle="Body copy, layout primitives, and content blocks for this content template."
                accent
                defaultOpen
              >
                <Stack spacing={2}>
                  <RunnerContentFields control={control} disabled={!canWrite} kind={runnerKind} />
                  <StepLayoutComposer campaign={campaign} canWrite={canWrite} runnerKind={runnerKind} />
                  {STEP_CONTENT_CATEGORIES[runnerKind]?.tree &&
                    NESTED_CONTENT_TREES[STEP_CONTENT_CATEGORIES[runnerKind].tree as string] && (
                      <NestedContentEditor
                        campaign={campaign}
                        canWrite={canWrite}
                        tree={NESTED_CONTENT_TREES[STEP_CONTENT_CATEGORIES[runnerKind].tree as string]}
                      />
                    )}
                  {STEP_CONTENT_CATEGORIES[runnerKind]?.categories.length > 0 && (
                    <ContentBlocksEditor
                      campaign={campaign}
                      canWrite={canWrite}
                      only={STEP_CONTENT_CATEGORIES[runnerKind].categories}
                      categoryLabels={STEP_CONTENT_CATEGORIES[runnerKind].labels}
                      title={STEP_CONTENT_CATEGORIES[runnerKind].title}
                      subtitle={STEP_CONTENT_CATEGORIES[runnerKind].subtitle}
                    />
                  )}
                </Stack>
              </CollapsibleSection>
            )}

            {/* The report template is a generic, opt-in content option any content step can integrate
                (surfaced via the `report-editor` layout primitive) — not a property of one template. It
                edits the campaign-scoped template, so it's available on every content step for maximum
                authoring flexibility. It is a page-scale tool, so it opens in a full-screen overlay
                rather than rendering inside the narrow dock. */}
            {stepKind === 'content' && !isPrepStep && (
              <SummaryRow
                title="Report template"
                meta="Optional. Sections, prose, layout, and the figure canvas for this campaign's report. Add the “report-editor” primitive to this step's layout to render it at runtime."
                action={
                  <GhostButton onClick={() => setReportOpen(true)}>Open report builder →</GhostButton>
                }
              />
            )}

            {stepKind === 'content' && !isPrepStep && OPTIONAL_CONTENT_SECTIONS.length > 0 && (
              <CollapsibleSection
                title="Optional content sections"
                subtitle="Add reference sections (e.g. FAQ) to this content step. None are added by default."
              >
                <Controller
                  name="content_sections"
                  control={control}
                  render={({ field }) => {
                    const enabled: string[] = Array.isArray(field.value) ? field.value : [];
                    const toggle = (category: string, on: boolean) => {
                      const next = on
                        ? Array.from(new Set([...enabled, category]))
                        : enabled.filter((c) => c !== category);
                      field.onChange(next);
                    };
                    return (
                      <Stack spacing={2}>
                        {OPTIONAL_CONTENT_SECTIONS.map((sec) => {
                          const on = enabled.includes(sec.category);
                          return (
                            <Box key={sec.category}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={on}
                                    disabled={!canWrite}
                                    onChange={(e) => toggle(sec.category, e.target.checked)}
                                  />
                                }
                                label={
                                  <Box>
                                    <Box component="span" sx={{ fontWeight: 600 }}>
                                      Add “{sec.label}” section
                                    </Box>
                                    <Box
                                      component="span"
                                      sx={{ display: 'block', fontSize: '0.75rem', color: 'text.secondary' }}
                                    >
                                      Renders via the “{sec.primitive}” primitive — add it to this step’s
                                      layout below to show the section in the runner.
                                    </Box>
                                  </Box>
                                }
                                sx={{ alignItems: 'flex-start', m: 0 }}
                              />
                              {on && (
                                <Box sx={{ mt: 1 }}>
                                  <ContentBlocksEditor
                                    campaign={campaign}
                                    canWrite={canWrite}
                                    only={[sec.category]}
                                    categoryLabels={{ [sec.category]: sec.label }}
                                    title={sec.title}
                                    subtitle={sec.subtitle}
                                  />
                                </Box>
                              )}
                            </Box>
                          );
                        })}
                      </Stack>
                    );
                  }}
                />
              </CollapsibleSection>
            )}

            {sectionRelevant('filesAndQuestions', stepKind) && (
              <CollapsibleSection
                title="Files & questions"
                subtitle="Attach campaign assets and question sets to this step."
              >
                <Stack spacing={2}>
                  <StepFilesFields
                    control={control}
                    campaignId={campaign.id}
                    runnerKind={runnerKind}
                    disabled={!canWrite}
                  />

                  <StepQuestionsFields
                    control={control}
                    campaignId={campaign.id}
                    disabled={!canWrite}
                    {...(runnerKind === 'deepdive'
                      ? {
                          title: 'Questions (exported report only)',
                          note:
                            'These predefined questions appear only in the exported deep-dive report — not on the on-screen deep-dive console. To edit the on-screen scenario questions, use the nested “Deep-dive scenarios” tree above.',
                        }
                      : {})}
                  />
                </Stack>
              </CollapsibleSection>
            )}

            {sectionRelevant('fileUploads', stepKind) && (
              <CollapsibleSection
                title="File uploads"
                subtitle="Optionally let people working a delivery attach files to this step. Files are stored per delivery and only campaign members can download them."
              >
                <FileUploadFields control={control} disabled={!canWrite} />
              </CollapsibleSection>
            )}

            <CollapsibleSection
              title="Behavior & completion"
              subtitle="Completion mode, gating, duration, requirement, and visibility."
            >
              <Stack spacing={2}>
            {isTopLevel && (
              <Controller
                name="completion_mode"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    label="Completion"
                    size="small"
                    disabled={!canWrite}
                    fullWidth
                    helperText={
                      field.value === 'per_delivery'
                        ? 'Delivered repeatedly — the SE creates a delivery per account and this step’s sub-steps are walked per delivery.'
                        : 'Completed once by the SE (pre-work / enablement).'
                    }
                  >
                    {COMPLETION_MODES.map((m) => (
                      <MenuItem key={m.value} value={m.value}>
                        {m.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            )}

            {showSalesforceDriven && (
              <Controller
                name="salesforce_driven"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={field.value}
                        disabled={!canWrite}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label={
                      <Box>
                        <Box component="span" sx={{ fontWeight: 600 }}>
                          Salesforce Account/Opportunity driven
                        </Box>
                        <Box
                          component="span"
                          sx={{ display: 'block', fontSize: '0.75rem', color: 'text.secondary' }}
                        >
                          On: each delivery picks a real Salesforce account (and optionally
                          opportunity). Off: the SE names the delivery with a plain title — for
                          one-to-many customer events.
                        </Box>
                      </Box>
                    }
                    sx={{ alignItems: 'flex-start', m: 0 }}
                  />
                )}
              />
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Controller
                name="estimated_duration_minutes"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Duration (min)"
                    type="number"
                    size="small"
                    disabled={!canWrite}
                    fullWidth
                  />
                )}
              />
              <Controller
                name="is_required"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={field.value}
                        disabled={!canWrite}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label="Required"
                    sx={{ whiteSpace: 'nowrap' }}
                  />
                )}
              />
            </Stack>

            {canGate && (
              <Controller
                name="blocks_progression"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={field.value}
                        disabled={!canWrite}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label={
                      <Box>
                        <Box component="span" sx={{ fontWeight: 600 }}>
                          Block progression until complete
                        </Box>
                        <Box
                          component="span"
                          sx={{ display: 'block', fontSize: '0.75rem', color: 'text.secondary' }}
                        >
                          Learners can’t advance past this step in the runner until its activity is
                          finished.
                        </Box>
                      </Box>
                    }
                    sx={{ alignItems: 'flex-start', m: 0 }}
                  />
                )}
              />
            )}

            <Controller
              name="hidden"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      disabled={!canWrite}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label={
                    <Box>
                      <Box component="span" sx={{ fontWeight: 600 }}>
                        Hidden
                      </Box>
                      <Box
                        component="span"
                        sx={{ display: 'block', fontSize: '0.75rem', color: 'text.secondary' }}
                      >
                        Hide this step from the runner without deleting it. For a group, its whole
                        subtree is hidden. It stays here in the builder so you can restore it.
                      </Box>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', m: 0 }}
                />
              )}
            />

              </Stack>
            </CollapsibleSection>

            {/* Kind-specific fields switch on the watched step_kind. */}
            <CollapsibleSection
              title="Activity settings"
              subtitle="Kind-specific configuration for this step."
              accent
              defaultOpen
            >
              <StepKindFields control={control} stepKind={stepKind} disabled={!canWrite} />
            </CollapsibleSection>

            <CollapsibleSection
              title="Help & resource links"
              subtitle="Optional per-step links shown in the runner."
            >
              <Controller
                name="help_links"
                control={control}
                render={({ field }) => (
                  <LinkListEditor
                    value={field.value ?? []}
                    onChange={field.onChange}
                    disabled={!canWrite}
                    help="Shown on this step only. Leave empty to show no help control here."
                  />
                )}
              />
            </CollapsibleSection>

            <Box>
              <GhostButton onClick={() => setShowPreview(true)}>
                👁 Preview step
              </GhostButton>
            </Box>

            <Stack
              direction="row"
              spacing={1}
              justifyContent="space-between"
              alignItems="center"
            >
              <Box>
                {isEdit && canWrite && (
                  <GhostButton onClick={onDelete} disabled={isSaving}>
                    Delete
                  </GhostButton>
                )}
              </Box>
              <Stack direction="row" spacing={1}>
                {onCancel && (
                  <GhostButton onClick={onCancel} disabled={isSaving}>
                    Cancel
                  </GhostButton>
                )}
                {canWrite && (
                  <PrimaryButton type="submit" disabled={isSaving}>
                    {isEdit ? 'Save step' : parentId != null ? 'Add sub-step' : 'Add step'}
                  </PrimaryButton>
                )}
              </Stack>
            </Stack>
          </Stack>
        </Box>

      {showPreview && (
        <FullScreenOverlay title="Step preview" onClose={() => setShowPreview(false)}>
          <StepPreview
            campaign={campaign}
            values={{
              name: watched.name,
              description: watched.description,
              track: watched.track,
              step_kind: watched.step_kind,
              runner_kind: watched.runner_kind,
              runner: watched.runner,
              help_links: watched.help_links,
              checklist: watched.checklist,
              questions: watched.questions,
              layout: watched.layout,
              launch_url: watched.launch_url,
              content_sections: watched.content_sections,
            }}
          />
        </FullScreenOverlay>
      )}
      {reportOpen && (
        <FullScreenOverlay title="Report builder" onClose={() => setReportOpen(false)}>
          {/* The report is assembled per delivery (its prose is filled with the live customer /
              opportunity / captured findings), so its token picker always offers delivery-scoped
              groups — even when the host content step is itself enablement (`once`). */}
          <TokenFieldProvider scope="delivery">
            <ReportTemplateWorkspace campaignId={campaign.id} canWrite={canWrite} />
          </TokenFieldProvider>
        </FullScreenOverlay>
      )}
    </div>
    </TokenFieldProvider>
  );
}

export default StepDetailForm;
